import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import ConfettiEffect from '../components/ConfettiEffect';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { saveUserEntry } from '../utils/db';
import { 
  playRocketThrusterSound, 
  playLaunchChime, 
  playWinFanfare, 
  playTickSound 
} from '../utils/soundEffects';
import { useGameExitRoute } from '../utils/navigation';

const { FiAward, FiSun, FiMoon, FiHome, FiRotateCcw, FiUser, FiArrowRight, FiCheck, FiSend } = FiIcons;

const ROCKET_CORES = [
  { id: 'lvm3', name: 'LVM3 Heavy Booster', badge: '🚀', desc: 'India\'s heavy lift rocket capable of carrying massive lunar modules.' },
  { id: 'pslv', name: 'PSLV Polar Workhorse', badge: '🛰️', desc: 'Record-holder rocket famous for launching 104 satellites in one flight!' },
  { id: 'gaganyaan', name: 'Gaganyaan Crew Vehicle', badge: '👨‍🚀', desc: 'Equipped with crew emergency escape systems to carry Indian astronauts.' }
];

const PAYLOAD_TYPES = [
  { id: 'moon', name: 'Lunar Polar Rover', icon: '🌕', reward: 'Lunar Explorer Science Pass' },
  { id: 'tele', name: 'Deep Space Telescope', icon: '🔭', reward: 'Cosmic Astronomer Science Pass' },
  { id: 'weather', name: 'Monsoon Weather Satellite', icon: '🌧️', reward: 'Atmospheric Scientist Science Pass' }
];

const RocketBuilderFlow = () => {
  const { exitRoute, exitTitle } = useGameExitRoute();
  const [step, setStep] = useState('info'); // 'info' | 'build' | 'launch' | 'orbit'
  const [kidName, setKidName] = useState('');
  const [classGrade, setClassGrade] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [selectedCore, setSelectedCore] = useState(ROCKET_CORES[0]);
  const [selectedPayload, setSelectedPayload] = useState(PAYLOAD_TYPES[0]);
  const [launchAltitude, setLaunchAltitude] = useState(0);
  const [couponCode, setCouponCode] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);

  const handleStartBuild = (e) => {
    e.preventDefault();
    if (!kidName.trim()) {
      alert("Please enter the student's name!");
      return;
    }
    setStep('build');
    playTickSound(700, 0.05);
  };

  const handleLaunchRocket = () => {
    setStep('launch');
    playRocketThrusterSound(2.5);

    // Simulate launch ascent
    let alt = 0;
    const interval = setInterval(() => {
      alt += 40;
      setLaunchAltitude(alt);
      if (alt >= 400) {
        clearInterval(interval);
        setTimeout(() => {
          const code = 'ISRO-' + Math.random().toString(36).substring(2, 7).toUpperCase();
          setCouponCode(code);

          saveUserEntry({
            name: kidName.trim(),
            mobile: parentPhone.trim() || 'Junior Scientist',
            classGrade: classGrade.trim() || 'Space Cadet',
            reward: `🚀 Junior Rocket Scientist (${selectedCore.name})`,
            couponCode: code,
            gameType: 'Astro-Engineer Rocket'
          });

          setStep('orbit');
          playLaunchChime();
          setTimeout(() => playWinFanfare(), 500);
        }, 600);
      }
    }, 150);
  };

  const handleReset = () => {
    setKidName('');
    setClassGrade('');
    setParentPhone('');
    setSelectedCore(ROCKET_CORES[0]);
    setSelectedPayload(PAYLOAD_TYPES[0]);
    setLaunchAltitude(0);
    setCouponCode('');
    setStep('info');
  };

  return (
    <div className={`min-h-screen transition-colors duration-500 flex flex-col justify-between p-4 md:p-6 select-none ${
      isDarkMode 
        ? 'bg-slate-950 text-slate-100' 
        : 'bg-gradient-to-br from-sky-50 via-slate-50 to-indigo-50 text-slate-800'
    }`}>
      {step === 'orbit' && <ConfettiEffect />}

      {/* Header */}
      <header className={`flex items-center justify-between pb-3 max-w-4xl mx-auto w-full border-b transition-colors ${
        isDarkMode ? 'border-slate-800' : 'border-slate-300 bg-white/70 backdrop-blur-md rounded-2xl px-4 shadow-sm'
      }`}>
        <div className="flex items-center gap-3">
          <Link to={exitRoute} title={exitTitle} className={`p-2.5 rounded-full border transition-all shadow-sm ${
            isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-300 hover:text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
          }`}>
            <SafeIcon icon={FiHome} className="text-xl" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">🛠️</span>
              <h1 className="text-xl md:text-2xl font-black bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 via-sky-600 to-indigo-600 dark:from-cyan-400 dark:via-sky-300 dark:to-indigo-400">
                Astro-Engineer: Rocket Builder
              </h1>
            </div>
            <p className={`text-[11px] font-bold uppercase tracking-wider ${
              isDarkMode ? 'text-cyan-400' : 'text-slate-600'
            }`}>
              Interactive Stall Kiosk • Build, Fuel & Launch Your Satellite
            </p>
          </div>
        </div>

        <button 
          onClick={() => setIsDarkMode(!isDarkMode)}
          className={`px-3 py-1.5 rounded-full text-xs font-bold border flex items-center gap-1.5 cursor-pointer shadow-md transition-all ${
            isDarkMode ? 'border-slate-700 bg-slate-900 text-amber-300' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
          }`}
        >
          <SafeIcon icon={isDarkMode ? FiSun : FiMoon} />
          <span>{isDarkMode ? 'Night Mode 🌌' : 'Day Mode ☀️'}</span>
        </button>
      </header>

      {/* Main Flow Content */}
      <main className="flex-1 flex flex-col items-center justify-center my-6 max-w-2xl mx-auto w-full">
        <AnimatePresence mode="wait">
          
          {/* Step 1: Cadet Registration */}
          {step === 'info' && (
            <motion.div
              key="info"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`w-full p-6 sm:p-8 rounded-3xl border shadow-2xl transition-all ${
                isDarkMode 
                  ? 'bg-slate-900/90 border-slate-700 text-white' 
                  : 'bg-white border-slate-300 text-slate-900 shadow-xl'
              }`}
            >
              <div className="text-center mb-6">
                <span className="text-5xl inline-block mb-2 animate-bounce">🚀</span>
                <h2 className={`text-2xl font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Welcome Future Astronaut!
                </h2>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Design and assemble your heavy-lift space rocket for orbit injection.
                </p>
              </div>

              <form onSubmit={handleStartBuild} className="space-y-4">
                <div>
                  <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 flex items-center gap-1.5 ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-600'
                  }`}>
                    <SafeIcon icon={FiUser} className="text-cyan-500" />
                    <span>Cadet / Student Name *</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={kidName}
                    onChange={e => setKidName(e.target.value)}
                    placeholder="e.g. Riya Patel"
                    className={`w-full px-4 py-3 rounded-2xl border text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all ${
                      isDarkMode 
                        ? 'border-slate-700 bg-slate-800 text-white' 
                        : 'border-slate-300 bg-slate-50 text-slate-900 focus:bg-white'
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-600'
                  }`}>
                    Class / Grade (Optional)
                  </label>
                  <input
                    type="text"
                    value={classGrade}
                    onChange={e => setClassGrade(e.target.value)}
                    placeholder="e.g. Grade 6 - B"
                    className={`w-full px-4 py-3 rounded-2xl border text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all ${
                      isDarkMode 
                        ? 'border-slate-700 bg-slate-800 text-white' 
                        : 'border-slate-300 bg-slate-50 text-slate-900 focus:bg-white'
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-xs font-bold uppercase tracking-wider mb-1.5 ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-600'
                  }`}>
                    Parent Phone (Optional for students)
                  </label>
                  <input
                    type="tel"
                    value={parentPhone}
                    onChange={e => setParentPhone(e.target.value)}
                    placeholder="e.g. 9876543210"
                    className={`w-full px-4 py-3 rounded-2xl border text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all ${
                      isDarkMode 
                        ? 'border-slate-700 bg-slate-800 text-white' 
                        : 'border-slate-300 bg-slate-50 text-slate-900 focus:bg-white'
                    }`}
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-4 mt-2 rounded-2xl bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-600 hover:from-cyan-400 hover:to-indigo-500 text-slate-950 font-black text-base shadow-lg shadow-cyan-500/20 flex items-center justify-center gap-2 cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  <span>Enter Assembly Hangar</span>
                  <SafeIcon icon={FiArrowRight} />
                </button>
              </form>
            </motion.div>
          )}

          {/* Step 2: Rocket Assembly & Engineering */}
          {step === 'build' && (
            <motion.div
              key="build"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={`w-full p-6 sm:p-8 rounded-3xl border shadow-2xl transition-all ${
                isDarkMode 
                  ? 'bg-slate-900/90 border-slate-700 text-white' 
                  : 'bg-white border-slate-300 text-slate-900 shadow-xl'
              }`}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <span className="text-[10px] font-black uppercase tracking-wider text-cyan-500">Step 1 of 2</span>
                  <h3 className={`text-xl font-black ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Assemble Rocket Vehicle</h3>
                </div>
                <span className={`text-xs font-bold px-3 py-1 rounded-full border ${
                  isDarkMode ? 'bg-slate-800 text-slate-300 border-slate-700' : 'bg-slate-100 text-slate-700 border-slate-300'
                }`}>
                  Cadet: {kidName}
                </span>
              </div>

              {/* Core Selection */}
              <div className="mb-6">
                <label className={`text-xs font-bold uppercase tracking-wider block mb-2 ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-600'
                }`}>
                  1. Choose Rocket Propulsion Core
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {ROCKET_CORES.map(core => (
                    <div
                      key={core.id}
                      onClick={() => {
                        setSelectedCore(core);
                        playTickSound(800, 0.04);
                      }}
                      className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                        selectedCore.id === core.id
                          ? isDarkMode 
                            ? 'bg-cyan-500/15 border-cyan-400 text-white shadow-lg shadow-cyan-500/10'
                            : 'bg-cyan-50 border-cyan-500 text-slate-900 shadow-md'
                          : isDarkMode
                            ? 'bg-slate-800/60 border-slate-700 text-slate-300 hover:border-slate-600'
                            : 'bg-slate-50 border-slate-200 text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      <div className="text-3xl mb-1">{core.badge}</div>
                      <div className="font-bold text-xs">{core.name}</div>
                      <div className={`text-[10px] mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{core.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payload Selection */}
              <div className="mb-8">
                <label className={`text-xs font-bold uppercase tracking-wider block mb-2 ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-600'
                }`}>
                  2. Choose Scientific Mission Payload
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {PAYLOAD_TYPES.map(payload => (
                    <div
                      key={payload.id}
                      onClick={() => {
                        setSelectedPayload(payload);
                        playTickSound(900, 0.04);
                      }}
                      className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                        selectedPayload.id === payload.id
                          ? isDarkMode
                            ? 'bg-amber-500/15 border-amber-400 text-white shadow-lg shadow-amber-500/10'
                            : 'bg-amber-50 border-amber-500 text-slate-900 shadow-md'
                          : isDarkMode
                            ? 'bg-slate-800/60 border-slate-700 text-slate-300 hover:border-slate-600'
                            : 'bg-slate-50 border-slate-200 text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      <div className="text-3xl mb-1">{payload.icon}</div>
                      <div className="font-bold text-xs">{payload.name}</div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={handleLaunchRocket}
                className="w-full py-4 rounded-2xl bg-gradient-to-r from-orange-500 via-amber-500 to-emerald-500 text-slate-950 font-black text-lg shadow-xl shadow-orange-500/20 flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer"
              >
                <span>🔥 Ignition & Launch to Orbit!</span>
              </button>
            </motion.div>
          )}

          {/* Step 3: Launch Sequence Ascent */}
          {step === 'launch' && (
            <motion.div
              key="launch"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={`w-full p-8 rounded-3xl border text-center shadow-2xl flex flex-col items-center transition-all ${
                isDarkMode ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-slate-300 text-slate-900 shadow-xl'
              }`}
            >
              <div className="text-xs font-black uppercase text-orange-500 tracking-widest mb-2 animate-pulse">
                Ascent Trajectory Live
              </div>
              <h3 className={`text-2xl font-black mb-6 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Altitude: {launchAltitude} km / 400 km
              </h3>

              {/* Animated Ascending Rocket */}
              <div className="relative w-24 h-48 flex flex-col items-center justify-center my-6">
                <motion.div
                  animate={{ y: [-10, 10, -10] }}
                  transition={{ repeat: Infinity, duration: 0.3 }}
                  className="text-6xl sm:text-7xl filter drop-shadow-[0_0_20px_rgba(249,115,22,0.8)]"
                >
                  🚀
                </motion.div>
                <div className="text-3xl mt-1 animate-bounce">🔥</div>
              </div>

              {/* Ascent Progress Bar */}
              <div className={`w-full max-w-md h-3 rounded-full overflow-hidden border p-0.5 mt-4 ${
                isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-100 border-slate-300'
              }`}>
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-orange-500 to-cyan-500"
                  style={{ width: `${(launchAltitude / 400) * 100}%` }}
                />
              </div>

              <p className={`text-xs mt-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Booster separation successful... Injecting into Low Earth Orbit!
              </p>
            </motion.div>
          )}

          {/* Step 4: Orbit Insertion & Certificate Pass */}
          {step === 'orbit' && (
            <motion.div
              key="orbit"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`w-full p-8 rounded-3xl border-2 text-center shadow-2xl transition-all ${
                isDarkMode 
                  ? 'bg-slate-900 border-cyan-400 text-white' 
                  : 'bg-white border-cyan-500 text-slate-900 shadow-xl'
              }`}
            >
              <div className="w-20 h-20 rounded-full bg-cyan-500/20 text-cyan-500 dark:text-cyan-300 flex items-center justify-center text-4xl mx-auto mb-3 shadow-xl">
                <SafeIcon icon={FiAward} />
              </div>

              <div className="text-xs font-black uppercase text-cyan-500 tracking-widest mb-1">
                Mission Accomplished!
              </div>

              <h2 className={`text-2xl sm:text-3xl font-black mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {kidName}
              </h2>

              <p className={`text-xs mb-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Successfully built and launched {selectedCore.name} with {selectedPayload.name} into space!
              </p>

              <div className={`p-4 rounded-2xl border mb-6 text-left space-y-2 ${
                isDarkMode ? 'bg-slate-800/80 border-cyan-500/40 text-slate-300' : 'bg-slate-50 border-cyan-200 text-slate-700'
              }`}>
                <div className="flex justify-between text-xs">
                  <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Mission Status:</span>
                  <span className="text-emerald-500 font-bold">Orbit Stable (400 km)</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Award Title:</span>
                  <span className="text-amber-500 font-bold">ISRO Junior Rocket Scientist</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Voucher Pass Code:</span>
                  <span className="text-cyan-600 dark:text-cyan-300 font-mono font-bold">{couponCode}</span>
                </div>
              </div>

              <button
                onClick={handleReset}
                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-cyan-500 to-indigo-600 text-white font-black text-sm flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer"
              >
                <SafeIcon icon={FiRotateCcw} />
                <span>Next Cadet's Mission</span>
              </button>
            </motion.div>
          )}

        </AnimatePresence>
      </main>

      <footer className="text-center text-xs text-slate-500 py-2 border-t border-slate-800">
        GamifyCenter STEM Academy • Space Technology & Rocket Engineering
      </footer>
    </div>
  );
};

export default RocketBuilderFlow;
